if (!customElements.get('lumino-related-products')) {
  class LuminoRelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.stage = this.querySelector('.lumino-related-products__stage');
      this.previous = this.querySelector('[data-related-prev]');
      this.next = this.querySelector('[data-related-next]');
      this.counter = this.querySelector('.lumino-related-products__counter');
      this.error = this.querySelector('.lumino-related-products__error');
      this.status = this.querySelector('[data-related-status]');
      this.cache = new Map();
      this.pending = new Map();
      this.controllers = new Set();
      this.batchSize = 7;
      this.rawTotal = 0;
      this.total = 0;
      this.currentPage = 1;
      this.currentStart = 0;
      this.excludedAt = null;
      this.requestToken = 0;
      this.loads = 0;
      this.columns = this.pageSize();
      this.stage.style.setProperty('--related-visible-columns', this.columns);
      this.stage.innerHTML = '<div class="lumino-related-products__placeholder" aria-hidden="true"></div>'.repeat(this.columns);
      this.previous.addEventListener('click', () => this.goPage(this.currentPage - 1));
      this.next.addEventListener('click', () => this.goPage(this.currentPage + 1));
      this.querySelector('[data-related-retry]').addEventListener('click', () => this.goPage(this.failedPage || 1));
      this.stage.addEventListener('keydown', event => {
        if (event.target.closest('input, select, textarea')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goPage(this.currentPage + (event.key === 'ArrowRight' ? 1 : -1));
      });
      this.stage.addEventListener('touchstart', event => {
        this.touchStart = event.touches.length === 1 ? {x: event.touches[0].clientX, y: event.touches[0].clientY} : null;
      }, {passive: true});
      this.stage.addEventListener('touchend', event => {
        if (!this.touchStart || event.changedTouches.length !== 1) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) this.goPage(this.currentPage + (dx < 0 ? 1 : -1));
      }, {passive: true});
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.started = true;
        this.goPage(1);
      }, {rootMargin: '200px 0px'});
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(entries => {
        const width = Math.round(entries[0].contentRect.width);
        if (width === this.previousWidth) return;
        this.previousWidth = width;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          const columns = this.pageSize();
          if (columns === this.columns) return;
          this.columns = columns;
          this.stage.style.setProperty('--related-visible-columns', Math.min(this.columns, this.total || this.columns));
          this.stage.style.minHeight = '';
          if (this.started) this.goPage(Math.floor(this.currentStart / this.columns) + 1, true);
        }, 120);
      });
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controllers?.forEach(controller => controller.abort());
      clearTimeout(this.resizeTimer);
    }

    pageSize() {
      const pager = this.querySelector('.lumino-related-products__pager');
      if (matchMedia('(max-width: 749px)').matches) {
        return Math.max(1, Math.floor((pager.clientWidth + 1) / 176));
      }
      return Number(getComputedStyle(pager).getPropertyValue('--related-columns')) || 3;
    }

    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.columns));
    }

    async sourcePage(page) {
      if (this.cache.has(page)) {
        const result = this.cache.get(page);
        this.cache.delete(page);
        this.cache.set(page, result);
        return result;
      }
      if (this.pending.has(page)) return this.pending.get(page);
      const root = this.dataset.rootUrl.replace(/\/?$/, '/');
      const url = new URL(`${root}collections/${encodeURIComponent(this.dataset.sourceHandle)}`, location.origin);
      url.searchParams.set('section_id', 'lumino-related-page');
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.set('page', page);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const controller = new AbortController();
      this.controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 15000);
      const request = (async () => {
        this.dataset.networkLoads = String(++this.loads);
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error('Related products request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = document.querySelector('[data-lumino-related-page]');
        if (!result || Number(result.dataset.page) !== page) throw new Error('Invalid related products page');
        this.rawTotal = Number(result.dataset.total);
        this.total = Math.max(0, this.rawTotal - (this.dataset.excludeCurrent === 'true' ? 1 : 0));
        const cards = [...result.querySelectorAll('template[data-related-card]')].map((template, index) => {
          const id = template.dataset.productId;
          if (id === this.dataset.productId) this.excludedAt = (page - 1) * this.batchSize + index;
          return {id, html: template.innerHTML};
        });
        this.cache.set(page, cards);
        while (this.cache.size > 12) this.cache.delete(this.cache.keys().next().value);
        this.dataset.cachedBatches = String(this.cache.size);
        this.dataset.totalProducts = String(this.total);
        return cards;
      })().finally(() => {
        clearTimeout(timeout);
        this.controllers.delete(controller);
        this.pending.delete(page);
      });
      this.pending.set(page, request);
      return request;
    }

    async range(start, count) {
      let cursor = start + (this.excludedAt !== null && this.excludedAt <= start ? 1 : 0);
      const result = [];
      while (result.length < count && cursor < this.rawTotal) {
        const page = Math.floor(cursor / this.batchSize) + 1;
        const cards = await this.sourcePage(page);
        if (!cards.length) break;
        if (!result.length && this.excludedAt !== null && this.excludedAt <= start) cursor = Math.max(cursor, start + 1);
        if (Math.floor(cursor / this.batchSize) + 1 !== page) continue;
        const offset = cursor % this.batchSize;
        for (let index = offset; index < cards.length && result.length < count; index++) {
          cursor = (page - 1) * this.batchSize + index + 1;
          if (cards[index].id !== this.dataset.productId) result.push(cards[index]);
        }
        if (cards.length < this.batchSize) break;
      }
      return result;
    }

    async goPage(requestedPage, resize = false) {
      if (this.busy && !resize) return;
      if (this.total && (requestedPage < 1 || requestedPage > this.totalPages())) return;
      const token = ++this.requestToken;
      this.busy = true;
      this.failedPage = requestedPage;
      this.error.hidden = true;
      this.previous.disabled = true;
      this.next.disabled = true;
      this.stage.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingLabel;
      try {
        if (!this.cache.size) await this.sourcePage(1);
        if (!this.total) {
          this.hidden = true;
          return;
        }
        const page = Math.min(Math.max(1, requestedPage), this.totalPages());
        const count = Math.min(this.columns, this.total);
        const start = Math.min((page - 1) * this.columns, Math.max(0, this.total - count));
        const cards = await this.range(start, count);
        if (token !== this.requestToken || !this.isConnected) return;
        if (!cards.length) throw new Error('Empty related products page');
        const restoreFocus = this.stage.contains(document.activeElement);
        const previousHeight = resize ? 0 : this.stage.getBoundingClientRect().height;
        const template = document.createElement('template');
        template.innerHTML = cards.map(card => card.html).join('');
        template.content.querySelectorAll('form template').forEach(formTemplate => {
          formTemplate.parentElement.append(formTemplate.content.cloneNode(true));
          formTemplate.remove();
        });
        this.stage.style.setProperty('--related-visible-columns', cards.length);
        this.stage.replaceChildren(template.content);
        this.stage.style.minHeight = `${Math.max(previousHeight, this.stage.getBoundingClientRect().height)}px`;
        this.currentPage = page;
        this.currentStart = start;
        this.querySelector('[data-related-current]').textContent = page;
        this.querySelector('[data-related-total]').textContent = this.totalPages();
        this.counter.hidden = this.totalPages() < 2;
        this.previous.hidden = this.next.hidden = this.totalPages() < 2;
        this.dataset.page = String(page);
        this.dataset.visibleProducts = String(cards.length);
        this.dataset.ready = 'true';
        if (restoreFocus) this.stage.focus({preventScroll: true});
      } catch (error) {
        if (token === this.requestToken && this.isConnected) this.error.hidden = false;
      } finally {
        if (token === this.requestToken) {
          this.busy = false;
          this.stage.setAttribute('aria-busy', 'false');
          this.status.textContent = '';
          this.previous.disabled = this.currentPage <= 1;
          this.next.disabled = !this.total || this.currentPage >= this.totalPages();
        }
      }
    }
  }
  customElements.define('lumino-related-products', LuminoRelatedProducts);
}
